import axios from 'axios';
import React, { useState } from 'react';
import { Container, TextField, Button, Box, Typography, Paper, Snackbar, Alert, List, ListItem, ListItemText } from '@mui/material';
import { Editor } from '@tinymce/tinymce-react';
import { API_URL, authHeaders } from '../api';

const NewsManagement = () => {
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [image, setImage] = useState(null);
    const [videoUrl, setVideoUrl] = useState('');
    const [sourceUrl, setSourceUrl] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
    const [newsId, setNewsId] = useState('');
    const [searchTitle, setSearchTitle] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isEditing, setIsEditing] = useState(false);

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
        setErrorSnackbarOpen(false);
    };

    // Search news by title
    const handleSearchNews = async () => {
        if (!searchTitle.trim()) return;

        try {
            const response = await axios.get(`${API_URL}/api/news/search`, { params: { title: searchTitle } });
            setSearchResults(response.data);
        } catch (error) {
            console.error('Error searching for news:', error);
            setErrorSnackbarOpen(true);
        }
    };

    // Fetch the selected news details for editing
    const handleSelectNews = async (id) => {
        try {
            const response = await axios.get(`${API_URL}/api/news/${id}`);
            const { title, content, videoUrl, imageUrl, sourceUrl } = response.data;
            setTitle(title);
            setContent(content);
            setVideoUrl(videoUrl);
            setSourceUrl(sourceUrl || '');
            setImage(imageUrl ? { preview: `${API_URL}/api/news/image/${imageUrl}` } : null);
            setNewsId(id);  // Set the newsId for updating
            setIsEditing(true);  // Set form to editing mode
            setSearchResults([]); // Clear search results
            setSearchTitle(''); // Clear search query
        } catch (error) {
            console.error('Error fetching news details for editing:', error);
            setErrorSnackbarOpen(true);
        }
    };
    

    // Handle form submission
    const handleSubmit = async (event) => {
        event.preventDefault();

        const newsData = new FormData();
        newsData.append('title', title);
        newsData.append('content', content);
        if (image && typeof image === 'object' && image.file) newsData.append('image', image.file);
        newsData.append('videoUrl', videoUrl);
        newsData.append('sourceUrl', sourceUrl);

        try {
            if (isEditing) {
                // Update existing news
                await axios.put(`${API_URL}/api/news/${newsId}`, newsData, {
                    headers: { ...authHeaders() }
                });
            } else {
                // Create new news post
                await axios.post(`${API_URL}/api/news`, newsData, {
                    headers: { ...authHeaders() }
                });
            }
            setSnackbarOpen(true);
            // Clear the form only when creating a new post
            if (!isEditing) {
                setTitle('');
                setContent('');
                setImage(null);
                setVideoUrl('');
                setSourceUrl('');
            }
        } catch (error) {
            console.error('Error managing news:', error);
            setErrorSnackbarOpen(true);
        }
    };

    return (
        <Container maxWidth="sm">
            <Paper elevation={3} sx={{ padding: 2, mt: 3, background: 'transparent' }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    {isEditing ? 'Edit News Post' : 'Create News Post'}
                </Typography>

                {/* Search for existing news by title */}
                <Box display="flex" gap={2} mb={3}>
                    <TextField
                        label="Search News by Title"
                        variant="outlined"
                        value={searchTitle}
                        onChange={(e) => setSearchTitle(e.target.value)}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearchNews}
                    >
                        Search
                    </Button>
                </Box>

                {/* Display search results */}
                {searchResults.length > 0 && (
                    <List>
                        {searchResults.map((news) => (
                            <ListItem button key={news.id} onClick={() => handleSelectNews(news.id)}>
                                <ListItemText primary={news.title} />
                            </ListItem>
                        ))}
                    </List>
                )}

                <form onSubmit={handleSubmit}>
                    <Box display="flex" flexDirection="column" gap={3}>
                        <TextField
                            label="Title"
                            variant="outlined"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            required
                        />
                        <Editor
                            apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                            value={content}
                            init={{
                                height: 300,
                                menubar: false,
                                plugins: [
                                    'advlist autolink lists link image charmap print preview anchor',
                                    'searchreplace visualblocks code fullscreen',
                                    'insertdatetime media table paste code help wordcount'
                                ],
                                toolbar:
                                    'undo redo | formatselect | bold italic backcolor | \
                                    alignleft aligncenter alignright alignjustify | \
                                    bullist numlist outdent indent | removeformat | help'
                            }}
                            onEditorChange={(newContent) => setContent(newContent)}
                        />
                        <TextField
                            label="Video URL"
                            variant="outlined"
                            value={videoUrl}
                            onChange={(e) => setVideoUrl(e.target.value)}
                            placeholder="Paste your video URL here"
                        />
                        <TextField
                            label="Original source URL"
                            variant="outlined"
                            value={sourceUrl}
                            onChange={(e) => setSourceUrl(e.target.value)}
                            placeholder="Link to the original Facebook post"
                        />
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setImage({ file: e.target.files[0], preview: URL.createObjectURL(e.target.files[0]) })}
                        />

                        {/* Display image preview if selected */}
                        {image && (
                            <Box mt={2}>
                                <Typography variant="body1">Image Preview:</Typography>
                                <img
                                    src={image.preview} 
                                    alt="Image Preview"
                                    style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'cover' }}
                                />
                            </Box>
                        )}

                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            sx={{ padding: '10px' }}
                            disabled={!title.trim() || !content.trim()}
                        >
                            {isEditing ? 'Update News' : 'Submit'}
                        </Button>
                    </Box>
                </form>

                {/* Success and Error Snackbar */}
                <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
                    <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
                        {isEditing ? 'News post updated successfully!' : 'News post created successfully!'}
                    </Alert>
                </Snackbar>
                <Snackbar open={errorSnackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
                    <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: '100%' }}>
                        Something went wrong. Please try again.
                    </Alert>
                </Snackbar>
            </Paper>
        </Container>
    );
};

export default NewsManagement;
